import React from 'react';
import {
  AgnesAutocomplete,
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesDatePickerInput,
  AgnesIconButton,
  AgnesModal,
  AgnesPageHeader,
  AgnesStatusBadge,
} from '@agnes/ui';
import { Eye, Receipt, RefreshCw } from 'lucide-react';
import { useDctfStore } from '@/stores/useDctfStore';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import UtilsMask from '@/utils/UtilsMask';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Any = any;

interface Declaracao {
  [k: string]: unknown;
  id: string;
  tipo: string;
  competencia: string;
  debitoTotal: string;
  creditosTotal: string;
  saldoAPagarTotal: string;
  zerada: boolean;
}

const fmtMoeda = (v?: string | number) =>
  v == null
    ? 'R$ 0,00'
    : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// modal com o master-detail (por código de receita) + DARF + DCOMP de UMA declaração.
function DetalheDctf() {
  const detalhe = useDctfStore((s) => s.detalhe);
  const carregandoDetalhe = useDctfStore((s) => s.carregandoDetalhe);

  const detalhes: Any[] = detalhe?.detalhes ?? [];

  return (
    <AgnesModal
      open={!!detalhe}
      title="Detalhes da declaração"
      maxWidth="lg"
      onClose={() => useDctfStore.getState().fecharDetalhe()}
    >
      {detalhe?.razaoSocial && (
        <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 text-sm">
          <div>
            <span className="text-agnes-text-secondary">Contribuinte: </span>
            <span className="font-medium">{detalhe.razaoSocial}</span>
          </div>
          <div>
            <span className="text-agnes-text-secondary">Recibo: </span>
            <span className="font-medium">{detalhe.numRecibo || '-'}</span>
          </div>
          {detalhe.indZerada && <AgnesStatusBadge status="info" label="Declaração zerada" dot />}
        </div>
      )}
      <AgnesDataTable<Any>
        loading={carregandoDetalhe}
        columns={[
          { key: 'codReceita', header: 'Cód. Receita', width: 100 },
          { key: 'ctDescricaoTributo', header: 'Tributo', width: 260 },
          { key: 'ctValor', header: 'Débito', width: 130, render: (r) => fmtMoeda(r.ctValor) },
          {
            key: 'vlTotalCred',
            header: 'Crédito',
            width: 130,
            render: (r) => fmtMoeda(r.vlTotalCred),
          },
          {
            key: 'saldoAPagar',
            header: 'Saldo a pagar',
            width: 130,
            render: (r) => fmtMoeda(r.saldoAPagar),
          },
          {
            key: 'pagamentoDarf',
            header: 'DARF pago',
            width: 130,
            render: (r) => (r.pagamentoDarf != null ? fmtMoeda(r.pagamentoDarf) : '-'),
          },
          {
            key: 'quitada',
            header: 'Situação',
            width: 110,
            render: (r) => (
              <AgnesStatusBadge
                status={r.quitada ? 'success' : 'warning'}
                label={r.quitada ? 'Quitada' : 'Pendente'}
                dot
              />
            ),
          },
          {
            key: 'compensacoes',
            header: 'DCOMP',
            width: 200,
            render: (r) =>
              r.hasDcomp ? (
                <span
                  title={(r.compensacoes ?? [])
                    .map((c: Any) => `${c.numDoc}: ${fmtMoeda(c.vlCompensado)}`)
                    .join(', ')}
                >
                  {(r.compensacoes ?? []).length} compensação(ões)
                </span>
              ) : (
                <span className="text-agnes-text-muted">-</span>
              ),
          },
        ]}
        data={detalhes}
        rowKey={(r) => r.id}
      />
    </AgnesModal>
  );
}

export default function PageDctf() {
  const cnpjCliente = useDctfStore((s) => s.cnpjCliente);
  const ano = useDctfStore((s) => s.ano);
  const mes = useDctfStore((s) => s.mes);
  const lista = useDctfStore((s) => s.lista);
  const total = useDctfStore((s) => s.total);
  const page = useDctfStore((s) => s.page);
  const size = useDctfStore((s) => s.size);
  const sortBy = useDctfStore((s) => s.sortBy);
  const sortOrder = useDctfStore((s) => s.sortOrder);
  const setSort = useDctfStore((s) => s.setSort);
  const carregando = useDctfStore((s) => s.carregando);
  const sincronizando = useDctfStore((s) => s.sincronizando);
  const empregadores = useEmpregadoresStore((s) => s.lista);

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
  }, []);

  const empLabel = (e: Any) =>
    `${e.company ?? e.socialName ?? e.fantasyName ?? ''} - ${UtilsMask.cnpj(e.cnpjFilial)}`;
  const empSel = empregadores.find((e: Any) => e.cnpjFilial === cnpjCliente);

  const competenciaValor = `${ano}-${String(mes).padStart(2, '0')}`;
  const onCompetenciaChange = (v: string) => {
    const [a, m] = v.split('-').map(Number);
    if (a && m) useDctfStore.getState().setCompetencia(a, m);
  };

  const columns: AgnesDataTableColumn<Declaracao>[] = [
    { key: 'tipo', header: 'Tipo', width: 140, sortable: true },
    { key: 'competencia', header: 'Competência', width: 130, sortable: true },
    { key: 'debitoTotal', header: 'Débito total', width: 150, sortable: true },
    { key: 'creditosTotal', header: 'Créditos total', width: 150, sortable: true },
    { key: 'saldoAPagarTotal', header: 'Saldo a pagar', width: 150, sortable: true },
    {
      key: 'zerada',
      header: 'Situação',
      width: 130,
      render: (r) => (
        <AgnesStatusBadge
          status={r.zerada ? 'info' : 'success'}
          label={r.zerada ? 'Zerada' : 'Com débito'}
          dot
        />
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center',
      toggleable: false,
      render: (r) => (
        <AgnesIconButton
          aria-label="Ver detalhes"
          size="sm"
          onClick={() => useDctfStore.getState().abrirDetalhe(String(r.id))}
        >
          <Eye className="h-4 w-4" />
        </AgnesIconButton>
      ),
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<Receipt />}
        title="Débitos e Créditos Tributários"
        subtitle="Monitore os débitos das contribuições previdenciárias e de contribuições destinadas a terceiros pagas ou devidas e possíveis créditos"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Encargos' },
          { label: 'Débitos e Créditos Tributários' },
        ]}
      />

      <AgnesCard className="">
        <AgnesCardContent className="flex items-stretch">
          <div className="flex flex-row items-end justify-between gap-4">
            <div className="flex-1">
              <AgnesAutocomplete
                label="Empregador"
                placeholder="Buscar por CNPJ ou razão social"
                options={empregadores}
                value={empSel ? [empSel] : []}
                onOptionToggle={(e: Any) =>
                  useDctfStore.getState().selecionarEmpregador(e.cnpjFilial)
                }
                getOptionLabel={empLabel}
                isOptionEqualToValue={(a: Any, b: Any) => a.cnpjFilial === b.cnpjFilial}
                noOptionsText="Nenhum empregador"
              />
            </div>

            <div className="">
              <AgnesDatePickerInput
                label="Competência"
                value={competenciaValor}
                onChange={(value) => onCompetenciaChange(value)}
              />
            </div>

            <AgnesButton
              variant="secondary"
              disabled={!cnpjCliente || sincronizando}
              startIcon={<RefreshCw className="h-4 w-4" />}
              onClick={() => useDctfStore.getState().sincronizar()}
            >
              {sincronizando ? 'Sincronizando...' : 'Sincronizar'}
            </AgnesButton>
          </div>

          <TableWrapper className="mt-4">
            <AgnesDataTable<Declaracao>
              columns={columns}
              data={lista}
              rowKey={(r) => String(r.id)}
              loading={carregando}
              sort={{ sortBy, sortOrder, onSortChange: setSort }}
              pagination={{
                page,
                pageSize: size,
                total,
                serverSide: true,
                onPageChange: (p: number) => useDctfStore.getState().setPage(p),
                onPageSizeChange: (s: number) => useDctfStore.getState().setSize(s),
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>

      <DetalheDctf />
    </>
  );
}
